<script setup lang="ts">
import Product from "@/models/Product";
import ProductImage from "@/models/ProductImage";
import LazyImage from "@/components/utils/LazyImage.vue";
import AddToCartButton from "@/components/buttons/AddToCartButton.vue";
import MainProductCardPromList from "~/components/common/product/MainProductCardPromList.vue";
// import MainSearchProductMonthSelector from "~/components/MainSearchProductMonthSelector.vue";
import SafeHtml from "@/components/utils/SafeHtml.vue";
import {
  breakpointsBootstrapV5,
  useBreakpoints,
  useElementHover,
  useFocus,
} from "@vueuse/core";
import { computed, ref } from "vue";
import { formatPrice } from "@/utils/pricing";
import ProductHeaderPromIcons from "~/components/common/product/ProductHeaderPromIcons.vue";
import ProductWishlistButton from "@/components/product/ProductWishlistButton.vue";
import CustomerWishlist from "@/models/CustomerWishlist";
import CustomerWishlistItem from "@/models/CustomerWishlistItem";
import auth from "@/services/AuthService";

// Determina si se usará la carga optimizada de todos los wishlists de los productos en lugar de hacerlo producto por producto.
const LAZY_WISHLIST_ENABLE = true;

const props = defineProps<{
  product: Product;
  wishlists?: CustomerWishlist[];
  wishlistItems?: CustomerWishlistItem[];
}>();

const cardEl = ref<HTMLElement>();
const isUserLogged = auth.isUserLogged();

const isCardHovered = useElementHover(cardEl);
const { focused: isCardFocused } = useFocus(cardEl);
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileSize = breakpoints.smallerOrEqual("md");
const isMonthDropdownOpened = ref(false);
const isOutOfStock = computed(() => props.product.stock_total === 0);
const shouldUseActiveVersion = computed(
  () => isMobileSize.value || isCardHovered.value || isCardFocused.value,
);
</script>
<template>
  <article
    ref="cardEl"
    class="product-card px-2 px-md-0"
    tabindex="0"
    :data-page="product.page"
  >
    <NuxtLink
      class="card card-lg border-light shadow-sm position-relative"
      :class="{ active: shouldUseActiveVersion }"
      :style="[isMonthDropdownOpened ? 'z-index: 10' : '']"
      :to="{ name: 'products', params: { slug: props.product.slug } }"
    >
      <div
        v-if="isOutOfStock"
        class="d-flex align-items-end justify-content-center w-100 h-100 position-absolute z-1"
      >
        <div
          class="position-absolute h-100 w-100 bg-dark-subtle opacity-25 z-0"
        ></div>
        <span class="fs-6 fw-bold text-black-50 z-1 mb-3"
          >AGOTADO TEMPORALMENTE</span
        >
      </div>
      <div
        v-if="
          isUserLogged && (LAZY_WISHLIST_ENABLE || (wishlists && wishlistItems))
        "
        class="position-absolute end-0 top-0 mt-1 me-1"
      >
        <product-wishlist-button
          :product="product"
          :init-wishlists="wishlists"
          :init-wishlist-items="wishlistItems"
        />
      </div>
      <div class="row g-0">
        <!-- Start: Card Image -->
        <div class="col-12">
          <div class="product-cover text-center p-2">
            <lazy-image
              :src="
                product.getCoverImage(ProductImage.Sizes.Small) || undefined
              "
              :alt="product.name || undefined"
              width="120"
              height="120"
            />
          </div>
        </div>
        <!--/ End: Card Image -->
        <!-- Start: Card Body -->
        <div class="col-12">
          <div class="card-body p-2 pt-0 px-md-3">
            <h5
              class="card-title text-secondary fs-6 fw-normal mb-0 text-info"
              :class="{
                'text-l2-truncate': !isMobileSize && !shouldUseActiveVersion,
                'text-l3-truncate': isMobileSize,
              }"
            >
              <span>{{ product.name }}</span>
            </h5>
            <div
              v-if="product.brand"
              class="product-brand d-inline-block text-center pe-2 mb-2"
            >
              <span class="fw-bold small">
                {{ product.brand.name }}
              </span>
            </div>
            <product-header-prom-icons class="mb-3" :product="product" />
            <p class="card-text mb-0 d-flex align-items-center gap-2 lh-normal">
              <span class="text-secondary small-sm fw-light">{{
                product.hasDiscount() ? "Normal" : "Precio"
              }}</span>
              <safe-html
                tag="span"
                :html="
                  formatPrice(
                    product.getFinalCommonPrice(),
                    true,
                    product.hasDiscount()
                      ? 'text-secondary small text-black-50 text-decoration-line-through'
                      : 'fs-6 fw-semibold',
                  )
                "
              />
            </p>
            <p
              v-if="product.hasDiscount()"
              class="card-text mt-md-1 mb-0 d-flex align-items-center gap-2 lh-normal"
            >
              <span class="text-secondary small-sm fw-light text-truncate">
                Promoción contado
              </span>
              <safe-html
                class="text-secondary fs-4 fw-bold text-danger"
                tag="span"
                :html="formatPrice(product.getFinalCurrentPrice())"
              />
              <span
                v-if="product.mwi_one_disc_percent > 0.1"
                class="text-danger fs-6"
              >
                ¡Descuento a un pago!
              </span>
            </p>
            <!-- <main-product-card-month-selector
              class="mt-2 mt-md-3"
              :product="product"
              @is-opened="isMonthDropdownOpened = $event"
            /> -->
          </div>
        </div>
        <!--/ End: Card Body -->
      </div>
      <!-- Start: Card Footer -->
      <div class="mt-auto">
        <div
          class="card-footer d-flex justify-content-between bg-transparent border-0 p-2"
        >
          <main-product-card-prom-list :product="product" />
          <add-to-cart-button
            v-slot="slotProps"
            :product="product"
            :btn-add-from-search="true"
            :to="$route.fullPath"
          >
            <button
              class="btn btn-outline-secondary rounded-0 text-nowrap align-self-stretch"
              :class="[isMobileSize ? 'btn-lg' : 'btn-lg']"
              :style="{
                '--bs-btn-bg': '#2B2D421C',
                '--bs-btn-color': '#4B4B4B',
                '--bs-btn-border-color': '#2B2D421C',
              }"
              @click.prevent="slotProps.click(1)"
            >
              &nbsp;<i class="bi bi-cart"></i>&nbsp;
            </button>
          </add-to-cart-button>
        </div>
      </div>
      <!--/ End: Card Footer -->
    </NuxtLink>
  </article>
</template>
<style lang="scss" scoped>
@import "bootstrap/scss/functions";
@import "bootstrap/scss/variables";
@import "bootstrap/scss/mixins";

$card-desktop-height: 415px;

.product-card {
  z-index: -1;
  height: auto;
  @include media-breakpoint-up(md) {
    height: $card-desktop-height;
  }
  cursor: pointer;
  .card {
    text-decoration: none;
    z-index: auto;
    min-height: 100%;
    max-height: $card-desktop-height;
    overflow: hidden;
    &.active {
      box-shadow: var(--bs-box-shadow) !important;
      z-index: 2;
      max-height: $card-desktop-height + 100px;
      transition: 0.3s max-height ease-in-out;
      animation-duration: 0.3s;
      animation-name: visible-card-after-height;
    }
  }
}
// Ayuda a que se pueda aplicar visible para elementos como dropdowns justo al terminar la transición de height.
@keyframes visible-card-after-height {
  from {
    overflow: hidden;
  }
  to {
    overflow: visible;
  }
}
.product-brand {
  text-transform: uppercase;
}
.product-cover {
  img {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 120px;
  }
  @include media-breakpoint-up(md) {
    img {
      max-height: 160px;
    }
  }
}
</style>
